import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import EmptyOperationalState from '../components/EmptyOperationalState';
import PageHeader from '../components/PageHeader';
import PaymentStatusBadge from '../components/PaymentStatusBadge';
import DataState from '../components/DataState';
import { useSession } from '../permissions/SessionContext';
import AcademyPayments from '../modules/academy/Payments';
import { fetchOwnerDashboard } from '../api/members';
import { colors, layout, radius } from '../design-system/tokens';

const num = (value) => Number(value || 0).toLocaleString('ko-KR');

const getScopedParams = (session) =>
  session?.user?.role === 'master' ? { storeId: session.store.id } : {};

const SUMMARY_CARDS = [
  {
    key: 'paymentFailed',
    label: '결제 확인',
    caption: '자동결제 실패',
    tone: 'danger',
  },
  {
    key: 'cancellationRequested',
    label: '구독 변화',
    caption: '해지 예약',
    tone: 'warning',
  },
  {
    key: 'activeSubscriptions',
    label: '활성 구독',
    caption: '현재 이용 중',
    tone: 'success',
  },
  {
    key: 'noVehicle',
    label: '정보 미완성',
    caption: '필수 정보 보완',
    tone: 'neutral',
  },
];

const PAYMENT_TASKS = [
  {
    key: 'payment_failed',
    title: '결제 실패 회원',
    countKey: 'paymentFailed',
    status: 'failed',
    label: '우선 확인',
    description: '자동결제가 멈춘 회원을 확인하고, 상세 이력에서 안내 또는 재시도 여부를 판단합니다.',
    action: '결제 실패 회원 보기',
    filter: 'payment_failed',
  },
  {
    key: 'cancelling',
    title: '해지 예약 회원',
    countKey: 'cancellationRequested',
    status: 'warning',
    label: '상태 확인',
    description: '해지 예정인 구독을 확인하고, 필요한 경우 만료 전 안내를 정리합니다.',
    action: '해지 예약 보기',
    filter: 'cancelling',
  },
  {
    key: 'no_vehicle',
    title: '정보 미완성 회원',
    countKey: 'noVehicle',
    status: 'neutral',
    label: '보완 필요',
    description: '이용에 필요한 기본 정보가 비어 있는 회원을 확인합니다.',
    action: '보완 대상 보기',
    filter: 'no_vehicle',
  },
];

const toneByName = (tone) => {
  if (tone === 'danger') return colors.status.danger;
  if (tone === 'warning') return colors.status.warning;
  if (tone === 'success') return colors.status.success;
  return colors.status.neutral;
};

const SummaryCard = ({ label, value, caption, tone = 'neutral' }) => {
  const palette = toneByName(tone);
  return (
    <Paper
      sx={{
        p: { xs: 2, sm: layout.cardPadding },
        minWidth: 0,
        borderColor: palette.border || colors.border.muted,
        bgcolor: tone === 'neutral' ? colors.surface : palette.bg,
      }}
    >
      <Typography variant="caption" sx={{ color: palette.fg, fontWeight: 800 }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ mt: 0.9, fontSize: 22, fontVariantNumeric: 'tabular-nums' }}>
        {num(value)}건
      </Typography>
      <Typography variant="caption" sx={{ color: colors.text.muted }}>
        {caption}
      </Typography>
    </Paper>
  );
};

const PaymentTasks = ({ summary }) => {
  const navigate = useNavigate();

  return (
    <Paper sx={{ p: { xs: 2, sm: layout.cardPadding }, minWidth: 0 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 0.35, sm: 1 }}
        sx={{ alignItems: { xs: 'flex-start', sm: 'baseline' }, justifyContent: 'space-between', mb: 1.5 }}
      >
        <Typography variant="subtitle2">확인할 결제 업무</Typography>
        <Typography variant="caption" sx={{ color: colors.text.muted }}>
          실행 작업은 회원 상세에서만 진행합니다.
        </Typography>
      </Stack>
      <Stack spacing={1}>
        {PAYMENT_TASKS.map((task) => {
          const count = Number(summary?.[task.countKey] || 0);
          return (
            <Box
              key={task.key}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) auto' },
                gap: { xs: 1, sm: 1.5 },
                alignItems: 'center',
                p: 1.5,
                borderRadius: `${radius.sm}px`,
                border: `1px solid ${colors.border.soft}`,
                bgcolor: count > 0 ? colors.canvas.subtle : colors.surface,
              }}
            >
              <Stack direction="row" spacing={1.25} sx={{ alignItems: 'flex-start', minWidth: 0 }}>
                <PaymentStatusBadge label={task.label} status={task.status} />
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={0.75} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {task.title}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: colors.text.primary, fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}
                    >
                      {num(count)}건
                    </Typography>
                  </Stack>
                  <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                    {task.description}
                  </Typography>
                </Box>
              </Stack>
              <Button
                size="small"
                variant="text"
                onClick={() => navigate(`/owner-v2/members?filter=${task.filter}`)}
                sx={{ justifySelf: { xs: 'flex-start', sm: 'flex-end' }, fontWeight: 800, whiteSpace: 'nowrap' }}
              >
                {task.action} →
              </Button>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
};

const PaymentDataNotice = () => (
  <Paper sx={{ p: { xs: 2, sm: layout.cardPadding }, minWidth: 0 }}>
    <Typography variant="subtitle2">거래별 결제 내역</Typography>
    <Typography variant="body2" sx={{ mt: 0.75, color: colors.text.secondary }}>
      거래별 금액, 영수증, 결제 승인번호는 first-party 결제 API가 연결된 뒤 표시합니다.
      지금은 실제 회원 상태에서 확인 가능한 결제 실패와 구독 변화만 보여줍니다.
    </Typography>
  </Paper>
);

const SettlementsView = () => (
  <Stack spacing={layout.cardGap}>
    <EmptyOperationalState
      variant="preparing"
      title="정산 데이터 연결 예정"
      description="입금 예정일, 수수료, 기간별 입금 내역은 모구 결제/정산 API가 연결된 뒤 first-party 데이터만 표시합니다."
    />
    <Paper sx={{ p: { xs: 2, sm: layout.cardPadding }, minWidth: 0 }}>
      <Typography variant="subtitle2">정산 화면에 들어올 항목</Typography>
      <Stack spacing={1} sx={{ mt: 1.25 }}>
        {['입금 예정일과 입금 계좌', '기간별 결제 성공 합계', '수수료와 실입금액', '정산 보류 또는 환불 영향'].map((item) => (
          <Stack key={item} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <PaymentStatusBadge label="예정" status="neutral" />
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              {item}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  </Stack>
);

const CarwashPayments = ({ initialTab = 'pay' }) => {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(() => (searchParams.get('tab') === 'settle' ? 'settle' : initialTab));
  const { session } = useSession();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await fetchOwnerDashboard(getScopedParams(session)));
    } catch (err) {
      setError(err?.response?.data?.message || '결제 확인 정보를 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => data?.summary ?? {}, [data?.summary]);

  return (
    <Box>
      <PageHeader
        title="결제 · 정산"
        description="실제 회원 상태를 바탕으로 결제 실패와 구독 변화를 먼저 확인합니다. 금액·입금 내역은 정산 API 연결 후 표시합니다."
      />
      <Stack
        direction="row"
        spacing={2}
        sx={{ borderBottom: `1px solid ${colors.border.soft}`, overflowX: 'auto' }}
      >
        {[
          { key: 'pay', label: '결제 확인' },
          { key: 'settle', label: '정산 준비' },
        ].map((item) => (
          <Button
            key={item.key}
            variant="text"
            onClick={() => setTab(item.key)}
            sx={{
              minHeight: 40,
              px: 0.75,
              borderRadius: 0,
              flexShrink: 0,
              color: tab === item.key ? colors.text.primary : colors.text.secondary,
              borderBottom: tab === item.key ? `2px solid ${colors.brand.primary}` : '2px solid transparent',
            }}
          >
            {item.label}
          </Button>
        ))}
      </Stack>

      {tab === 'pay' ? (
        <DataState loading={loading} error={error} onRetry={load} skeleton={{ rows: 5, cols: 4 }}>
          <Stack spacing={layout.cardGap} sx={{ mt: layout.cardGap }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
                gap: layout.cardGap,
              }}
            >
              {SUMMARY_CARDS.map((card) => (
                <SummaryCard
                  key={card.key}
                  label={card.label}
                  value={summary[card.key]}
                  caption={card.caption}
                  tone={card.tone}
                />
              ))}
            </Box>
            <PaymentTasks summary={summary} />
            <PaymentDataNotice />
          </Stack>
        </DataState>
      ) : (
        <Box sx={{ mt: layout.cardGap }}>
          <SettlementsView />
        </Box>
      )}
    </Box>
  );
};

const Payments = ({ initialTab }) => {
  const { session } = useSession();

  if (session?.store?.category === 'academy') return <AcademyPayments />;

  return <CarwashPayments initialTab={initialTab} />;
};

export { CarwashPayments };
export default Payments;
